import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Heading, Text } from "@nbs/ui";
import { EmptyState } from "@/components/EmptyState";
import { SkeletonBlock } from "@/components/Skeleton";
import { ReviewErrorState } from "@/features/review/components/ReviewErrorState";
import { useSessionExpiryRedirect } from "@/features/review/hooks/useSessionExpiryRedirect";
import { N3MesuresList } from "../components/N3MesuresList";
import { useN3Mesures } from "../hooks/useN3Mesures";

export interface N3TargetPageProps {
  /** Already parsed and normalized by `parseMesureIdsParam` — never empty (see `N3Page`). */
  readonly mesureIds: readonly string[];
}

/**
 * The "`mesureIds` present" branch of `/n3` (§3.4–§3.6). `N3Page` (the
 * route scaffold) is the only caller, and only once
 * `parseMesureIdsParam(searchParams.get("mesureIds"))` returned a
 * non-empty array — the bare-`/n3` scaffold behaviour is untouched (see
 * `N3Page.tsx`).
 *
 * Five real states, in priority order: loading → auth error (reused
 * `ReviewErrorState`/`isAuthErrorCode`, never a bespoke one, §3.6) →
 * generic GraphQL/network error (same component, non-auth code) → none
 * of the requested ids resolved (`EmptyState`, "ressource introuvable",
 * §3.5) → at least one resolved (`N3MesuresList`, which itself surfaces
 * the "some ids missing" partial notice, §3.5 "à défaut de convention").
 * `useSessionExpiryRedirect` is the exact same hook `ReviewDetailPage`/
 * `N1CockpitPage` already use — no new session-expiry logic here.
 */
export function N3TargetPage({ mesureIds }: N3TargetPageProps) {
  const { t } = useTranslation();
  const { mesures, notFoundCount, isInitialLoading, error, refetch } = useN3Mesures(mesureIds);
  useSessionExpiryRedirect(error);

  return (
    <section className="nbs-n3-target-page nbs-detail-page" aria-labelledby="n3-target-heading">
      <header className="nbs-detail-page__header">
        <Link to="/n1" className="nbs-detail-page__back">
          {t("n3.target.backToCockpit")}
        </Link>
        <Heading level={1} id="n3-target-heading">
          {t("nav.n3")}
        </Heading>
        <Text tone="muted">{t("n3.target.description")}</Text>
      </header>

      {isInitialLoading ? (
        <>
          <Text tone="muted" role="status">
            {t("n3.target.loadingTitle")}
          </Text>
          <SkeletonBlock lines={4} />
        </>
      ) : error ? (
        <ReviewErrorState error={error} onRetry={() => void refetch()} />
      ) : mesures.length === 0 ? (
        <EmptyState title={t("n3.target.notFoundTitle")} body={t("n3.target.notFoundBody")} />
      ) : (
        <N3MesuresList mesures={mesures} notFoundCount={notFoundCount} />
      )}
    </section>
  );
}
